import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  Layers,
  BarChart3,
  Sparkles,
  Receipt,
  Settings as SettingsIcon,
  ShieldCheck,
  LifeBuoy,
  Users,
  Megaphone,
  Ticket,
  Cpu,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/app", label: "Übersicht", icon: LayoutDashboard, testid: "nav-overview", end: true },
  { to: "/app/journal", label: "Trading Journal", icon: BookOpen, testid: "nav-journal" },
  { to: "/app/strategien", label: "Strategien", icon: Layers, testid: "nav-strategies" },
  { to: "/app/analyse", label: "Analyse", icon: BarChart3, testid: "nav-analytics" },
  { to: "/app/ki-coach", label: "KI-Coach", icon: Sparkles, testid: "nav-coach" },
  { to: "/app/steuer", label: "Steuer-Tool", icon: Receipt, testid: "nav-tax" },
  { to: "/app/einstellungen", label: "Einstellungen", icon: SettingsIcon, testid: "nav-settings" },
];

const ADMIN_NAV = [
  { to: "/app/support", label: "Support-Tickets", icon: LifeBuoy, testid: "nav-admin-support" },
  { to: "/app/mitglieder", label: "Mitglieder", icon: Users, testid: "nav-admin-members" },
  { to: "/app/broadcast", label: "Broadcast", icon: Megaphone, testid: "nav-admin-broadcast" },
  { to: "/app/rabattcodes", label: "Rabattcodes", icon: Ticket, testid: "nav-admin-coupons" },
];

export default function Sidebar({ onNavigate }) {
  const { user, isAdmin, realUser } = useAuth();
  const displayName = user && user !== false ? (user.name || user.email?.split("@")[0]) : "Gast";
  const email = user && user !== false ? user.email : "";

  return (
    <nav
      className="flex flex-col w-full h-full p-5"
      aria-label="Hauptnavigation"
      data-testid="sidebar-nav"
    >
      <div className="px-2 py-2">
        <Logo variant="name" height={60} to="/app" testid="sidebar-logo" />
        <div className="text-xs text-foreground/70 mt-2 pl-1 tracking-wide">Intelligence Platform</div>
      </div>

      <div className="mt-8 mb-2 overline px-2">Bereiche</div>
      <ul className="flex-1 space-y-1">
        {NAV.map((n) => (
          <li key={n.to}>
            <NavLink
              to={n.to}
              end={n.end}
              onClick={onNavigate}
              data-testid={n.testid}
              className="nav-link"
              aria-label={n.label}
            >
              {({ isActive }) => (
                <span
                  className="flex items-center gap-3 w-full"
                  data-active={isActive || undefined}
                >
                  <n.icon className="h-4 w-4" strokeWidth={1.5} />
                  <span>{n.label}</span>
                </span>
              )}
            </NavLink>
          </li>
        ))}
        {isAdmin && (
          <>
            <li className="pt-4 overline px-2 text-[10px]" aria-hidden>Admin</li>
            {ADMIN_NAV.map((n) => (
              <li key={n.to}>
                <NavLink to={n.to} onClick={onNavigate} data-testid={n.testid} className="nav-link" aria-label={n.label}>
                  {({ isActive }) => (
                    <span className="flex items-center gap-3 w-full" data-active={isActive || undefined}>
                      <n.icon className="h-4 w-4" strokeWidth={1.5} />
                      <span>{n.label}</span>
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </>
        )}
        {realUser?.ami_owner && (
          <>
            <li className="pt-4 overline px-2 text-[10px]" aria-hidden>Privat</li>
            <li>
              <NavLink to="/app/ami" onClick={onNavigate} data-testid="nav-ami" className="nav-link" aria-label="Algorithmic Market Intelligence">
                {({ isActive }) => (
                  <span className="flex items-center gap-3 w-full" data-active={isActive || undefined}>
                    <Cpu className="h-4 w-4" strokeWidth={1.5} />
                    <span className="leading-tight">Algorithmic Market Intelligence</span>
                  </span>
                )}
              </NavLink>
            </li>
          </>
        )}
      </ul>

      <div className="mt-6 rounded-xl border border-border bg-secondary/60 p-4" data-testid="sidebar-user-card">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Angemeldet als
          </div>
          {isAdmin && (
            <span
              data-testid="sidebar-admin-badge"
              className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/20 border border-primary/40 text-primary-foreground"
            >
              <ShieldCheck className="h-3 w-3" strokeWidth={1.75} /> Admin
            </span>
          )}
        </div>
        <div className="mt-1 font-medium truncate" data-testid="sidebar-user-name">{displayName}</div>
        {email && <div className="text-xs text-muted-foreground truncate" data-testid="sidebar-user-email">{email}</div>}
      </div>
    </nav>
  );
}
